import exp from "constants";
import { CouponType } from "../config/enums";

export interface APIResponse<T = any> {
    statusCode: number;
    message: string;
    data?: T;
}

export interface UserSession {
    userName: string;
    token: string;
    roles: string[];
    expiresIn: number;
    expiryTimeStamp: string;
}

export interface Role {
    id: string;
    name: string;
    normalizedName: string;
    permission?: string[];
    users?: User[];
}

export type Register = {
    fullName: string;
    mobile: string;
    title: string;
    email: string;
    countryISOTwo?: string;
    password: string;
    confirmPassword: string;
    UserType: ClientType;
    Address: string;
    placeOfBirth: string;
    dateOfBirth: string;
    gender: Gender;
};

export enum ClientType {
    Owner = "Owner",
    Agent = "Agent",
}

export enum Gender {
    Male = "Male",
    Female = "Female",
}

export type User = {
    id: string;
    fullname: string;
    email: string;
    phoneNumber: string;
    isPhoneNumberConfirmed: boolean;
    createdDate: string;
    updatedDate: string | null;
    password: string;
};

//UserRole
export type UserRole = {
    userId: string;
};

export type Order = {
    id: string;
    orderId: string;
    quantity: number;
    totalPrice: number;
    status: string;
    shippingAddress: string | null;
    country: string | null;
    paymentMethod: string | null;
    trackingNumber: string | null;
    deliveryDate: string;
    purchaseDate: string;
    shippingCost: number;
    shippingDate: string;
    shipmentProvider: string;
    estimatedDeliveryDate: string;
    actualDeliveryDate: string;
    createdDate: string;
    rejectedReason: string;
    clientId: string;
    client: User;
    productId: string;
    product: Product;
};

export type Reject = {
    orderId: string;
    rejectedReason: string;
};

export enum OrderStatus {
    Requested = "Requested",
    Processing = "Processing",
    Purchased = "Purchased",
    InTransit = "InTransit",
    Delivered = "Delivered",
    Rejected = "Rejected",
    Cancelled = "Cancelled",
}

export type Product = {
    productId: string;
    name: string;
    description: string;
    price: number;
    categoryId: string;
    category: Category;
    supplier: string | null;
    cubicMeter: number;
    weight: number;
    imagePath: string | null;
    createdUser: string;
    updatedUser: string | null;
    createdDate: string | null;
    updatedDate: string | null;
    deletedDate: string | null;
    id: string;
};

export type Track = {
    id: string;
    trackId: string;
    orderId: string;
    order?: Order;
    status?: OrderStatus;
    location: string;
    remarks?: string;
    createdDate?: string;
};

export type PurchaseOrder = {
    id: string;
    quantity: number;
    price: number;
    categoryId: string;
    category?: Category;
    supplier?: string;
    weight: number;
    cubicMeter: number;
};

export type Shipment = {
    trackingNumber: string;
    shipmentProvider: string;
    estimatedDeliveryDate: string;
    shippingCost: number;
};

export type Billing = {
    id: string;
    BillingId: string;
    debit: number;
    credit: number;
    billingType: string;
    createdDate: string;
    createdUser: string;
    remarks: string;
    orderId: string;
    client: string;
};

export type Category = {
    id: string;
    name: string;
    description?: string;
    createdDate: string;
};

export type Client = {
    id?: string;
    customeId?: string;
    fullname: string;
    email: string;
    mobile: string;
    country?: string;
    countryISOTwo?: string;
    address?: string;
    placeOfBirth?: string;
    gender: string;
    dateOfBirth: string;
    clientType: string;
    motherName?: string;
    isActive: boolean;
    createdBy?: string;
    createdDate?: string;
};

export type RegisterClient = {
    fullName: string;
    mobile: string;
    title: string;
    email: string;
    password: string;
    confirmPassword: string;
    type?: string;
    phoneNumber: string;
    Address: string;
    PlaceOfBirth: string;
    Gender: string;
    DateOfBirth: string;
    IdIssueDate: string;
    IdExpiryDate: string;
    ClientType: string;
    MotherName: string;
    IsActive: boolean;
};

export type Notification = {
    id?: string;
    title: string;
    notificationType: NotificationType;
    email: string;
    phoneNumber: string;
    whatsApp: string;
    message: string;
    sentDate: string;
    sentBy: string;
};

export enum NotificationType {
    Email = "Email",
    WhatsApp = "WhatsApp",
}

export type SystemSetting = {
    id?: string;
    name: string;
    subTitle: string;
    heroDescription: string;
    servicesTitle: string;
    servicesDescription: string;
    aboutTitle: string;
    aboutDescription: string;
    contactOne: string;
    whatsApp: string;
    address: string;
    email: string;
    contactTwo: string;
    contactThree: string;
    facebook: string;
    twitter: string;
    instagram: string;
    linkedIn: string;
    youTube: string;
    description: string;
    image: string;
    icon: string;
    createdDate?: string;
    createdUser?: string;
};

export type Partner = {
    id?: string;
    name: string;
    subTitle: string;
    description: string;
    image: string;
    createdDate: string;
    createdUser: string;
};

export type Service = {
    id?: string;
    name: string;
    subTitle: string;
    description: string;
    image: string;
    icon: string;
    createdDate: string;
    createdUser: string;
};

export interface TrackResponse {
    statusCode: number;
    message: string;
    data: Track[];
}

export type UsersTableDataType = User;

export type StatType = {
    id: number;
    count: number | string;
    icon: React.ReactNode;
    label: string;
};

export interface Coupon {
    id: string;
    name: string;
    type: CouponType;
    slug: string;
    amount?: string;
    code?: string;
}

export interface Address {
    customerName?: string;
    phoneNumber?: string;
    country?: string;
    state?: string;
    city?: string;
    zip?: string;
    street?: string;
}

export interface GoogleMapLocation {
    lat?: number;
    lng?: number;
    street_number?: string;
    route?: string;
    street_address?: string;
    city?: string;
    state?: string;
    country?: string;
    zip?: string;
    formattedAddress?: string;
}

export type ProductColor = {
    name?: string;
    code?: string;
};

export interface CartItem {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    image: string;
    color?: ProductColor | null;
    price: number;
    salePrice?: number;
    quantity: number;
    size: number;
    stock?: number;
    discount?: number;
}

export type Item = {
    id: number;
    slug?: string;
    title: string;
    description?: string;
    price: number;
    sale_price?: number;
    thumbnail: string;
    colors?: ProductColor[];
    sizes?: number[];
};

export type PosProduct = {
    id: number;
    name: string;
    description: string;
    image: string;
    price: number;
    salePrice: number;
    quantity: number;
    size: number;
    discount?: number;
};
export interface CalendarEvent {
    id?: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    title: string;
    description?: string;
    location?: string;
}

export interface FlightingCardProps {
    id: number;
    image: string;
    title: string;
    price: string;
    meta?: {
        model: string;
        hours: string;
        stop: string;
    };
    class: string;
    bucket: {
        luggage?: string;
        bag?: string;
    };
    airlines?: string;
    routes?: {
        arrivalDate: Date | string;
        arrivalTime: Date | string;
        departureDate: Date | string;
        departureTime: Date | string;
        departureCityCode: string;
        departureCity: string;
        departureTerminal: string;
        arrivalCityCode: string;
        arrivalCity: string;
        arrivalTerminal: string;
        layover: {
            layoverCityCode: string;
            layoverCity: string;
            layoverTerminal: string;
            layoverTime: string;
        }[];
    };
    cheapest?: boolean;
    best?: boolean;
    quickest?: boolean;
}

export type LayoverAirPortOptionType = {
    id: number;
    name: string;
    isGroupTitle?: boolean;
    checked?: boolean;
    disabled?: boolean;
};

export type TanTableProductsDataType = {
    id: string;
    name: string;
    category: string;
    image: string;
    price: string;
    quantity: number;
};
